// src/hooks/useStatistics.js - version corrigée
import {useEffect, useState} from 'react';
import {onValue, ref} from 'firebase/database';
import {database} from '../config/firebase';
import {useAuth} from './useAuth';

export const useStatistics = (churchId = null) => {
    const [statistics, setStatistics] = useState({
        global: null,
        church: null,
        professions: null
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const {currentUser, userRole, loading: authLoading} = useAuth();

    useEffect(() => {
        // Ne pas essayer de charger les statistiques tant que l'authentification n'est pas terminée
        if (authLoading) {
            return;
        }

        if (!currentUser) {
            setStatistics({global: null, church: null, professions: null});
            setLoading(false);
            return;
        }

        let globalStatsUnsubscribe;
        let churchStatsUnsubscribe;
        let professionsStatsUnsubscribe;

        const fetchStatistics = () => {
            try {
                setLoading(true);

                // Toujours récupérer les statistiques globales
                const globalStatsRef = ref(database, 'statistics/global');
                globalStatsUnsubscribe = onValue(globalStatsRef, (snapshot) => {
                    if (snapshot.exists()) {
                        setStatistics(prev => ({...prev, global: snapshot.val()}));
                    } else {
                        setStatistics(prev => ({...prev, global: null}));
                    }
                }, (error) => {
                    console.error("Erreur lors de la récupération des statistiques globales:", error);
                    setError(error.message);
                });

                // Déterminer quelle église à surveiller
                let targetChurchId = churchId;

                // Vérifier que userRole existe avant d'accéder à ses propriétés
                if (!targetChurchId && userRole?.role === 'church_admin') {
                    targetChurchId = userRole.churchId;
                }

                if (targetChurchId) {
                    const churchStatsRef = ref(database, `statistics/churches/${targetChurchId}`);
                    churchStatsUnsubscribe = onValue(churchStatsRef, (snapshot) => {
                        if (snapshot.exists()) {
                            setStatistics(prev => ({...prev, church: snapshot.val()}));
                        } else {
                            setStatistics(prev => ({...prev, church: null}));
                        }
                    }, (error) => {
                        console.error("Erreur lors de la récupération des statistiques d'église:", error);
                        setError(error.message);
                    });
                } else if (userRole?.role === 'admin') {
                    // Pour les admins, récupérer toutes les statistiques d'église
                    const allChurchesStatsRef = ref(database, 'statistics/churches');
                    churchStatsUnsubscribe = onValue(allChurchesStatsRef, (snapshot) => {
                        if (snapshot.exists()) {
                            setStatistics(prev => ({...prev, church: snapshot.val()}));
                        } else {
                            setStatistics(prev => ({...prev, church: null}));
                        }
                    }, (error) => {
                        console.error("Erreur lors de la récupération des statistiques d'églises:", error);
                        setError(error.message);
                    });
                }

                // Récupérer les statistiques de profession
                const professionsStatsRef = ref(database, 'statistics/professions');
                professionsStatsUnsubscribe = onValue(professionsStatsRef, (snapshot) => {
                    if (snapshot.exists()) {
                        setStatistics(prev => ({...prev, professions: snapshot.val()}));
                    } else {
                        setStatistics(prev => ({...prev, professions: null}));
                    }

                    setLoading(false);
                }, (error) => {
                    console.error("Erreur lors de la récupération des statistiques de professions:", error);
                    setError(error.message);
                    setLoading(false);
                });
            } catch (error) {
                console.error("Erreur:", error);
                setError(error.message);
                setLoading(false);
            }
        };

        fetchStatistics();

        return () => {
            if (globalStatsUnsubscribe) globalStatsUnsubscribe();
            if (churchStatsUnsubscribe) churchStatsUnsubscribe();
            if (professionsStatsUnsubscribe) professionsStatsUnsubscribe();
        };
    }, [currentUser, userRole, churchId, authLoading]); // Ajout de authLoading aux dépendances

    // Transformer les données pour les graphiques
    const getGenderDistributionData = () => {
        const data = churchId && statistics.church ? statistics.church : statistics.global;
        if (!data) return [];

        return [
            {name: "Hommes", value: data.hommes || 0},
            {name: "Femmes", value: data.femmes || 0}
        ];
    };

    const getBaptizedDistributionData = () => {
        const data = churchId && statistics.church ? statistics.church : statistics.global;
        if (!data) return [];

        return [
            {name: "Baptisés", value: data.baptises || 0},
            {name: "Non-baptisés", value: (data.totalMembres || 0) - (data.baptises || 0)}
        ];
    };

    const getProfessionsData = () => {
        if (!statistics.professions) return [];

        return Object.entries(statistics.professions).map(([name, count]) => ({
            name,
            value: count
        })).sort((a, b) => b.value - a.value);
    };

    return {
        statistics,
        loading: loading || authLoading, // Considérer le composant comme en chargement tant que l'auth n'est pas terminée
        error,
        getGenderDistributionData,
        getBaptizedDistributionData,
        getProfessionsData
    };
};
